export type AbstractFilterViewArgs = {
	readonly page?: number;
	readonly rows?: number;
};

const DEFAULT_PAGE = 0;
const DEFAULT_ROWS = 25;

/**
 * Base for immutable list filter Views: carries paging (server-side, page indexed at 0) and exposes
 * `queryParams` as the single source of truth for a server-filtered list request. A concrete filter
 * view's own fields mirror its constructor `Args` one for one (same convention as a `FormData`'s
 * `cloneWith`), which is what lets the setters below rebuild an instance from `this` without ever
 * hardcoding the subclass name.
 */
abstract class AbstractFilterView<Args extends AbstractFilterViewArgs = AbstractFilterViewArgs> {
	readonly page: number;
	readonly rows: number;

	protected constructor(args: Args) {
		this.page = args.page ?? DEFAULT_PAGE;
		this.rows = args.rows ?? DEFAULT_ROWS;
	}

	get queryParams(): URLSearchParams {
		const params = new URLSearchParams();
		params.set('page', String(this.page));
		params.set('rows', String(this.rows));
		return params;
	}

	setPage(page: number): this {
		return this.cloneWith({ page } as Partial<Args>);
	}

	setRows(rows: number): this {
		return this.cloneWith({ rows } as Partial<Args>);
	}

	setPageAndRows(page: number, rows: number): this {
		return this.cloneWith({ page, rows } as Partial<Args>);
	}

	/**
	 * Rebuilds the concrete subclass from its own current fields, patched, via `this.constructor`
	 * rather than a hardcoded class name — the same reasoning as `cloneWith` on a decorated
	 * `FormData` (`reference/patterns/immutable-classes.md`), even though a filter view is a plain
	 * class, not a decorated one.
	 */
	protected cloneWith(patch: Partial<Args>): this {
		const ConcreteFilterView = this.constructor as new (args: Args) => this;
		return new ConcreteFilterView({ ...(this as unknown as Args), ...patch });
	}
}

export default AbstractFilterView;
